/**
 * Webtech HR Engine™ - AI Insights Page
 * Page de dashboard et insights IA pour RH et DG
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlan } from '../contexts/PlanContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { FeatureGate, ModuleGate, ProBadge, AIBadge, LockedOverlay } from '../components/FeatureGate';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Brain,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Users,
  Target,
  AlertTriangle,
  CheckCircle2,
  Lightbulb,
  BarChart3,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Shield,
  Star,
  Zap,
  Award,
  BookOpen,
  UserCheck,
  Clock,
  Crown
} from 'lucide-react';

export default function AIInsightsPage() {
  const { user } = useAuth();
  const { isPro, canAccessAI, loading: planLoading } = usePlan();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [teamAnalysis, setTeamAnalysis] = useState(null);
  const [skillsGaps, setSkillsGaps] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (!planLoading) {
      fetchData();
    }
  }, [planLoading, isPro]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch AI dashboard data
      const dashboardRes = await api.get('/ai/dashboard');
      setDashboard(dashboardRes.data);
      
      // Fetch departments
      const employeesRes = await api.get('/employees?limit=10000');
      const employeesList = employeesRes.data.employees || employeesRes.data || [];
      const depts = [...new Set(employeesList.map(e => e.department).filter(Boolean))];
      setDepartments(depts);
      
      // If PRO, use latest team analysis from dashboard response
      if (dashboardRes.data.aiEnabled && dashboardRes.data.latestTeamAnalysis) {
        setTeamAnalysis(dashboardRes.data.latestTeamAnalysis);
      }
    } catch (error) {
      console.error('Error fetching AI dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const runTeamAnalysis = async () => {
    setAnalyzing(true);
    try {
      const dept = selectedDepartment === 'all' ? null : selectedDepartment;
      const response = await api.post('/ai/team/analyze', null, {
        params: { department: dept }
      });
      setTeamAnalysis(response.data.analysis);
      toast.success('Analyse IA terminée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const runSkillsGapsAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await api.post('/ai/skills-gaps/analyze');
      setSkillsGaps(response.data.analysis);
      toast.success('Analyse des compétences terminée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setAnalyzing(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      excellence: 'bg-green-100 text-green-700',
      progression: 'bg-blue-100 text-blue-700',
      stagnation: 'bg-amber-100 text-amber-700',
      'sous-performance': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getRiskColor = (risk) => {
    const colors = {
      faible: 'text-green-600',
      moyen: 'text-amber-600',
      élevé: 'text-red-600'
    };
    return colors[risk] || 'text-slate-600';
  };

  if (loading || planLoading) {
    return (
      <DashboardLayout title="Insights IA">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  // If not PRO, show upgrade prompt
  if (!isPro || !dashboard?.aiEnabled) {
    return (
      <DashboardLayout title="Insights IA">
        <ModuleGate 
          module="aiAnalysis"
          title="Insights IA & Reporting Avancé"
          description="Accédez à l'analyse IA de vos données RH, aux recommandations automatisées et aux tableaux de bord décisionnels."
        >
          <div />
        </ModuleGate>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Insights IA">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Brain className="w-7 h-7 text-purple-600" />
              Insights IA
              <AIBadge />
            </h1>
            <p className="text-slate-500">Analyses intelligentes et recommandations RH</p>
          </div>
          <div className="flex gap-2">
            <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toute l'entreprise</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={runTeamAnalysis}
              disabled={analyzing}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {analyzing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyse en cours...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Nouvelle analyse</>
              )}
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Effectif analysé</p>
                  <p className="text-xl font-bold">
                    {dashboard?.performance?.analyzedCount || 0}/{dashboard?.employeeCount || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Score perf. moyen</p>
                  <p className="text-xl font-bold">{dashboard?.performance?.averageScore || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Score compét. moyen</p>
                  <p className="text-xl font-bold">{dashboard?.skills?.averageScore || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Excellence</p>
                  <p className="text-xl font-bold">
                    {dashboard?.performance?.statusDistribution?.excellence || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Sous-performance</p>
                  <p className="text-xl font-bold">
                    {dashboard?.performance?.statusDistribution?.['sous-performance'] || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Insufficient Data Warning */}
        {!dashboard?.hasSufficientData && (
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">Données insuffisantes pour une analyse complète</h3>
                  <p className="text-amber-700 text-sm mb-3">
                    Pour obtenir une analyse RH pertinente, veuillez vous assurer que :
                  </p>
                  <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
                    <li>Des campagnes d'évaluation ont été complétées avec les évaluations manager</li>
                    <li>Les profils de compétences des employés sont renseignés</li>
                    <li>Des plans de développement sont en cours ou complétés</li>
                  </ul>
                  <div className="mt-4 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate('/performance')}>
                      Voir les campagnes
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => navigate('/skills/team')}>
                      Voir les compétences
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="performance">Performance</TabsTrigger>
            <TabsTrigger value="skills">Compétences</TabsTrigger>
            <TabsTrigger value="recommendations">Recommandations</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            {(teamAnalysis || dashboard?.latestTeamAnalysis) ? (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Executive Summary */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600" />
                      Synthèse IA
                      {(teamAnalysis?.insufficientData || dashboard?.latestTeamAnalysis?.insufficientData) && (
                        <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">
                          Données limitées
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(teamAnalysis?.insufficientData || dashboard?.latestTeamAnalysis?.insufficientData) ? (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-6 border border-amber-200">
                        <div className="flex items-start gap-3 mb-4">
                          <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                          <div>
                            <p className="text-lg text-amber-800 font-medium mb-2">
                              Analyse RH incomplète
                            </p>
                            <p className="text-slate-700">
                              {(teamAnalysis || dashboard?.latestTeamAnalysis)?.executiveSummary}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 p-4 bg-white/50 rounded-lg">
                          <p className="text-sm font-medium text-slate-700 mb-2">État des données :</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div className="text-center">
                              <p className="text-2xl font-bold text-blue-600">
                                {(teamAnalysis || dashboard?.latestTeamAnalysis)?.dataStatus?.employees || dashboard?.employeeCount || 0}
                              </p>
                              <p className="text-slate-500">Employés</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-green-600">
                                {(teamAnalysis || dashboard?.latestTeamAnalysis)?.dataStatus?.evaluatedEmployees || dashboard?.performance?.analyzedCount || 0}
                              </p>
                              <p className="text-slate-500">Évalués</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-purple-600">
                                {(teamAnalysis || dashboard?.latestTeamAnalysis)?.dataStatus?.skillProfiles || dashboard?.skills?.profilesCount || 0}
                              </p>
                              <p className="text-slate-500">Profils compét.</p>
                            </div>
                            <div className="text-center">
                              <p className="text-2xl font-bold text-amber-600">
                                {(teamAnalysis || dashboard?.latestTeamAnalysis)?.dataStatus?.activePlans || dashboard?.development?.activePlans || 0}
                              </p>
                              <p className="text-slate-500">Plans actifs</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6">
                          <p className="text-lg text-slate-700 leading-relaxed">
                            {(teamAnalysis || dashboard?.latestTeamAnalysis)?.executiveSummary}
                          </p>
                        </div>
                        <div className="mt-6 grid grid-cols-3 gap-4">
                          <div className="text-center p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-500 mb-1">Score de santé RH</p>
                            <p className="text-3xl font-bold text-webtech-blue">
                              {(teamAnalysis || dashboard?.latestTeamAnalysis)?.globalHealthScore}%
                            </p>
                          </div>
                          <div className="text-center p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-500 mb-1">Risque turnover</p>
                            <p className={`text-xl font-bold ${getRiskColor((teamAnalysis || dashboard?.latestTeamAnalysis)?.hrKPIs?.turnoverRisk)}`}>
                              {(teamAnalysis || dashboard?.latestTeamAnalysis)?.hrKPIs?.turnoverRisk || 'N/A'}
                            </p>
                          </div>
                          <div className="text-center p-4 bg-slate-50 rounded-lg">
                            <p className="text-sm text-slate-500 mb-1">Engagement</p>
                            <p className={`text-xl font-bold ${getRiskColor((teamAnalysis || dashboard?.latestTeamAnalysis)?.hrKPIs?.engagementLevel === 'élevé' ? 'faible' : (teamAnalysis || dashboard?.latestTeamAnalysis)?.hrKPIs?.engagementLevel)}`}>
                              {(teamAnalysis || dashboard?.latestTeamAnalysis)?.hrKPIs?.engagementLevel || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Performance Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Répartition des performances
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(teamAnalysis.performanceDistribution || {}).map(([key, value]) => (
                        <div key={key} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="capitalize">{key.replace('sousPerformance', 'Sous-performance')}</span>
                            <span className="font-medium">{value}%</span>
                          </div>
                          <Progress value={value} className={`h-2 ${
                            key === 'excellence' ? '[&>div]:bg-green-500' :
                            key === 'progression' ? '[&>div]:bg-blue-500' :
                            key === 'stagnation' ? '[&>div]:bg-amber-500' :
                            '[&>div]:bg-red-500'
                          }`} />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Key Risks */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Risques identifiés
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {(teamAnalysis.keyRisks || []).map((risk, idx) => (
                        <li key={idx} className="flex items-start gap-2 p-2 bg-red-50 rounded-lg">
                          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-red-700">{risk}</span>
                        </li>
                      ))}
                      {(!teamAnalysis.keyRisks || teamAnalysis.keyRisks.length === 0) && (
                        <li className="text-slate-500 text-sm">Aucun risque majeur identifié</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>

                {/* Key Strengths */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      Points forts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {(teamAnalysis.keyStrengths || []).map((strength, idx) => (
                        <li key={idx} className="flex items-start gap-2 p-2 bg-green-50 rounded-lg">
                          <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-green-700">{strength}</span>
                        </li>
                      ))}
                      {(!teamAnalysis.keyStrengths || teamAnalysis.keyStrengths.length === 0) && (
                        <li className="text-slate-500 text-sm">Analyse en cours...</li>
                      )}
                    </ul>
                  </CardContent>
                </Card>

                {/* Strategic Recommendations */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-500" />
                      Recommandations stratégiques
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {(teamAnalysis.strategicRecommendations || []).map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-amber-700 font-bold text-sm">{idx + 1}</span>
                          </div>
                          <p className="text-sm text-amber-800">{rec}</p>
                        </div>
                      ))}
                    </div>
                    {teamAnalysis.urgentActions && teamAnalysis.urgentActions.length > 0 && (
                      <div className="mt-4 p-4 bg-red-50 rounded-lg border border-red-200">
                        <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Actions urgentes
                        </h4>
                        <ul className="space-y-1">
                          {teamAnalysis.urgentActions.map((action, idx) => (
                            <li key={idx} className="text-sm text-red-700">• {action}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Brain className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    Aucune analyse disponible
                  </h3>
                  <p className="text-slate-500 mb-4">
                    Lancez une nouvelle analyse IA pour obtenir des insights sur votre équipe.
                  </p>
                  <Button onClick={runTeamAnalysis} disabled={analyzing}>
                    {analyzing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analyse en cours...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Lancer l'analyse</>
                    )}
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance">
            <Card>
              <CardHeader>
                <CardTitle>Analyse de performance par employé</CardTitle>
                <CardDescription>
                  Sélectionnez un employé pour voir son analyse IA détaillée
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center py-8">
                <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">
                  Accédez aux analyses individuelles depuis le module Performance.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => navigate('/performance')}
                >
                  Aller au module Performance
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Skills Tab */}
          <TabsContent value="skills" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5" />
                      Analyse des écarts de compétences
                    </CardTitle>
                    <CardDescription>
                      Vue globale des compétences et des gaps à combler
                    </CardDescription>
                  </div>
                  <Button onClick={runSkillsGapsAnalysis} disabled={analyzing}>
                    {analyzing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {skillsGaps ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Préparation globale</p>
                        <p className="text-3xl font-bold text-webtech-blue">
                          {skillsGaps.overallReadiness}%
                        </p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Gaps critiques</p>
                        <p className="text-3xl font-bold text-red-600">
                          {skillsGaps.criticalGapsCount}
                        </p>
                      </div>
                      <div className="text-center p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 mb-1">Compétences analysées</p>
                        <p className="text-3xl font-bold text-slate-700">
                          {skillsGaps.rawData?.totalSkills || 0}
                        </p>
                      </div>
                    </div>
                    
                    {skillsGaps.topPrioritySkills && skillsGaps.topPrioritySkills.length > 0 && (
                      <div>
                        <h4 className="font-medium text-slate-700 mb-3">Compétences prioritaires</h4>
                        <div className="space-y-2">
                          {skillsGaps.topPrioritySkills.map((skill, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <Badge className="bg-amber-100 text-amber-700">{idx + 1}</Badge>
                                <span className="font-medium">{skill.skill}</span>
                              </div>
                              <span className="text-sm text-amber-700">{skill.action}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-slate-600 italic">
                      {skillsGaps.strategicInsights}
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500 mb-4">
                      Lancez une analyse pour identifier les écarts de compétences.
                    </p>
                    <Button onClick={runSkillsGapsAnalysis} disabled={analyzing}>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyser les compétences
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Recommendations Tab */}
          <TabsContent value="recommendations">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-500" />
                  Toutes les recommandations IA
                </CardTitle>
              </CardHeader>
              <CardContent>
                {teamAnalysis ? (
                  <div className="space-y-6">
                    {teamAnalysis.urgentActions && teamAnalysis.urgentActions.length > 0 && (
                      <div>
                        <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                          <Zap className="w-4 h-4" />
                          Actions urgentes
                        </h4>
                        <div className="space-y-2">
                          {teamAnalysis.urgentActions.map((action, idx) => (
                            <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                              <p className="text-sm text-red-800">{action}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="font-medium text-slate-700 mb-3">Recommandations stratégiques</h4>
                      <div className="space-y-2">
                        {(teamAnalysis.strategicRecommendations || []).map((rec, idx) => (
                          <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {skillsGaps?.trainingBudgetPriorities && (
                      <div>
                        <h4 className="font-medium text-slate-700 mb-3">Priorités formation</h4>
                        <div className="flex flex-wrap gap-2">
                          {skillsGaps.trainingBudgetPriorities.map((skill, idx) => (
                            <Badge key={idx} variant="outline" className="py-1">
                              <BookOpen className="w-3 h-3 mr-1" />
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Lightbulb className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      Les recommandations apparaîtront après une analyse IA.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
